import { Inject, Injectable, Type } from '@angular/core'
import { Dialog, DialogRef } from '@angular/cdk/dialog'
import { Overlay } from '@angular/cdk/overlay'
import { Observable } from 'rxjs'
import { CaDialogComponent } from './ca-dialog.base'

/**
 * Podmnožina ngx-simple-modal options které kód reálně potřebuje. Zbytek (animationDuration,
 * draggable, autoFocus, wrapperClass…) je řešen CDK / CSS a ignoruje se.
 */
export interface SimpleModalOptionsOverrides {
    closeOnEscape?: boolean;
    closeOnClickOutside?: boolean;
}

/**
 * CDK-backed náhrada za `SimpleModalService` z abandoned `@looorent/ngx-simple-modal`.
 *
 * Drop-in: stejné API (`addModal` / `removeModal` / `removeAll`) i stejný název třídy, takže
 * call-sity mění pouze import path. Komponenty musí dědit z `CaDialogComponent` (parita s
 * původním `SimpleModalComponent`).
 *
 * Data se na instanci nastaví přes `Object.assign` hned po `dialog.open()` (před prvním change
 * detection / ngOnInit) — replikace `mapDataObject` semantiky, nezávislé na `useDefineForClassFields`.
 *
 * Konfigurace overlay viz `CaDialogComponent` doc: `hasBackdrop: false` (komponenty si nesou
 * vlastní `.curtain`), `scrollStrategy: noop()` (žádný scroll-block jump). `@angular/cdk/overlay-prebuilt.css`
 * + `.cdk-overlay-container { z-index: 20000 }` override jsou v global stylech.
 */
@Injectable({
    providedIn: 'root',
})
export class SimpleModalService {

    private readonly openRefs = new Map<CaDialogComponent<unknown, unknown>, DialogRef<unknown>>()

    constructor(@Inject(Dialog) private dialog: Dialog,
                @Inject(Overlay) private overlay: Overlay) {
    }

    addModal<T, T1>(component: Type<CaDialogComponent<T, T1>>, data?: T, options?: SimpleModalOptionsOverrides): Observable<T1> {
        const ref = this.dialog.open<T1>(component, {
            data: data,
            hasBackdrop: false,
            autoFocus: false,
            disableClose: options?.closeOnEscape === false,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
        })

        const instance = ref.componentInstance as CaDialogComponent<unknown, unknown>
        if (instance) {
            if (data) {
                Object.assign(instance, data)
            }
            this.openRefs.set(instance, ref as DialogRef<unknown>)
            ref.closed.subscribe(() => this.openRefs.delete(instance))
        }

        return ref.closed as Observable<T1>
    }

    removeModal(component: CaDialogComponent<unknown, unknown>): Promise<unknown> {
        const ref = this.openRefs.get(component)
        if (ref) {
            ref.close()
            this.openRefs.delete(component)
        }
        return Promise.resolve({})
    }

    removeAll(): Promise<unknown> {
        this.dialog.closeAll()
        this.openRefs.clear()
        return Promise.resolve({})
    }
}